import AppConfig from 'app-config';
import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { session } from 'auth/session';
import { AuthError } from 'auth/errors';

type ProtectedConfig = AxiosRequestConfig & { authRetried?: boolean; sessionId?: string };
const client = axios.create({
  baseURL: AppConfig.ApiBaseUrl,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' }
});

client.interceptors.request.use(async (config: ProtectedConfig) => {
  if (!AppConfig.ApiBaseUrl) throw new AuthError('Dashboard API URL is not configured.', 503);
  const requestedSession = config.sessionId || session.metadata?.id;
  const token = await session.getAccessToken();
  if (requestedSession && requestedSession !== session.metadata?.id)
    throw new AuthError('Session changed.', 409);
  return {
    ...config,
    sessionId: session.metadata?.id,
    headers: { ...config.headers, Authorization: `Bearer ${token}` }
  };
});
client.interceptors.response.use(
  (response) => {
    if ((response.config as ProtectedConfig).sessionId !== session.metadata?.id) {
      return Promise.reject(
        new AuthError('Session changed; the previous response was discarded.', 409)
      );
    }
    return response;
  },
  async (error: AxiosError) => {
    const config = error.config as ProtectedConfig | undefined;
    if (error.response?.status !== 401 || !config) return Promise.reject(error);
    if (config.sessionId !== session.metadata?.id)
      return Promise.reject(new AuthError('Session changed.', 409));
    if (config.authRetried) {
      await session.exclusive(async () => {
        if (config.sessionId === session.metadata?.id) session.clear();
      });
      throw new AuthError('Your session is no longer authorized. Please sign in again.', 401);
    }
    const oldToken = String(config.headers?.Authorization || '').replace(/^Bearer /, '');
    const token = await session.getAccessToken(oldToken);
    if (config.sessionId !== session.metadata?.id) throw new AuthError('Session changed.', 409);
    return client({
      ...config,
      authRetried: true,
      headers: { ...config.headers, Authorization: `Bearer ${token}` }
    } as ProtectedConfig);
  }
);

export default client;
